import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Lock, EyeOff, Heart } from 'lucide-react'
import { useAuth } from '@/features/auth'
import { RecordAttachmentsDisplay } from './RecordAttachments'
import type { PatientRecordWithRelations } from '@repo/shared'

interface RecordCardProps {
  record: PatientRecordWithRelations
  patientId: string
  onEdit?: (record: PatientRecordWithRelations) => void
  onDelete?: (record: PatientRecordWithRelations) => void
  onToggleVisibility?: (record: PatientRecordWithRelations) => void
}

export function RecordCard({
  record,
  patientId,
  onEdit,
  onDelete,
  onToggleVisibility,
}: RecordCardProps) {
  const { userProfile, currentOrganization, isAdmin } = useAuth()
  const isAuthor = userProfile?.id === record.author_id
  const canManageRecords =
    currentOrganization?.userRole === 'admin' ||
    currentOrganization?.userRole === 'professional'
  const isPrivate = record.visibility === 'private'
  const isFamily = record.visibility === 'family'
  const isEdited = record.created_at !== record.updated_at

  // Use record_date if available, fallback to created_at
  const displayDate = record.record_date ?? record.created_at
  // Check if record_date differs from created_at by more than 1 minute
  const recordDateDiffersFromCreatedAt = record.record_date
    ? Math.abs(new Date(record.record_date).getTime() - new Date(record.created_at).getTime()) > 60000
    : false

  return (
    <div className="card p-4 space-y-3">
      {/* Header: author + date + visibility */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          {/* Author initial avatar */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-medium">
            {record.author?.full_name?.charAt(0)?.toUpperCase() ?? '?'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {record.author?.full_name ?? 'Profissional'}
            </p>
            <div className="flex flex-col text-xs text-gray-500">
              <span>
                {format(new Date(displayDate), "dd 'de' MMM 'de' yyyy 'as' HH:mm", {
                  locale: ptBR,
                })}
              </span>
              {recordDateDiffersFromCreatedAt && (
                <span className="text-gray-400">
                  Registrado em{' '}
                  {format(new Date(record.created_at), "dd/MM/yyyy 'as' HH:mm", {
                    locale: ptBR,
                  })}
                </span>
              )}
              {isEdited && (
                <span className="text-gray-400">
                  (editado em{' '}
                  {format(new Date(record.updated_at), "dd/MM/yyyy 'as' HH:mm", {
                    locale: ptBR,
                  })}
                  )
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Visibility indicator */}
          {isFamily && (
            <div className="flex items-center gap-1 text-xs text-teal-600" title="Compartilhado com familia">
              <Heart className="h-3.5 w-3.5" />
              <span>Familia</span>
            </div>
          )}
          {isPrivate && isAuthor && (
            <div className="flex items-center gap-1 text-xs text-amber-600" title="Registro privado">
              <Lock className="h-3.5 w-3.5" />
              <span>Privado</span>
            </div>
          )}
          {isPrivate && !isAuthor && isAdmin && (
            <div
              className="flex items-center gap-1 text-xs text-gray-400"
              title={`Registro privado de ${record.author?.full_name ?? 'outro profissional'}`}
            >
              <EyeOff className="h-3.5 w-3.5" />
              <span>Privado</span>
            </div>
          )}

          {/* Action menu (author only) */}
          {isAuthor && canManageRecords && (onEdit || onDelete || onToggleVisibility) && (
            <RecordActions
              record={record}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleVisibility={onToggleVisibility}
            />
          )}
        </div>
      </div>

      {/* Tags */}
      {record.tags && record.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {record.tags.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
              style={{
                backgroundColor: tag.color ? `${tag.color}20` : '#f3f4f6',
                color: tag.color ?? '#6b7280',
              }}
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}

      {/* Content (rendered HTML from Tiptap) */}
      <div
        className="prose prose-sm max-w-none text-gray-700 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
        dangerouslySetInnerHTML={{ __html: record.content }}
      />

      {/* Attachments */}
      {record.attachments && record.attachments.length > 0 && (
        <RecordAttachmentsDisplay
          attachments={record.attachments}
          patientId={patientId}
        />
      )}
    </div>
  )
}

// ============================================================
// Action menu sub-component
// ============================================================

function RecordActions({
  record,
  onEdit,
  onDelete,
  onToggleVisibility,
}: {
  record: PatientRecordWithRelations
  onEdit?: (record: PatientRecordWithRelations) => void
  onDelete?: (record: PatientRecordWithRelations) => void
  onToggleVisibility?: (record: PatientRecordWithRelations) => void
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-[9999] w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
            {onEdit && (
              <button
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                onClick={() => {
                  onEdit(record)
                  setOpen(false)
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
                Editar
              </button>
            )}
            {onToggleVisibility && (
              <button
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                onClick={() => {
                  onToggleVisibility(record)
                  setOpen(false)
                }}
              >
                {record.visibility === 'private' ? (
                  <>
                    <Eye className="h-3.5 w-3.5" />
                    Compartilhar
                  </>
                ) : (
                  <>
                    <Lock className="h-3.5 w-3.5" />
                    Tornar privado
                  </>
                )}
              </button>
            )}
            {onDelete && (
              <button
                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                onClick={() => {
                  onDelete(record)
                  setOpen(false)
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Excluir
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

import { useState } from 'react'
import { MoreVertical, Pencil, Eye, Trash2 } from 'lucide-react'
